import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Activity, TrendingUp, LineChart, Gauge, Target, CalendarClock, ShieldAlert, Sigma, Bot } from "lucide-react";
import { cn } from "../lib/utils";

function WeightRing({ label, pct, tint }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5 flex flex-col items-center text-center">
      <div className="text-3xl font-black font-mono" style={{ color: tint }}>{pct}%</div>
      <div className="text-sm font-semibold mt-1">{label}</div>
      <div className="h-1.5 w-full bg-secondary rounded-full mt-3 overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: tint }} />
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, tint, children }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-6">
      <div className="flex items-center gap-3 mb-3">
        <div className="h-10 w-10 rounded-xl flex items-center justify-center border"
          style={{ background: `${tint}1a`, borderColor: `${tint}44` }}>
          <Icon className="h-5 w-5" style={{ color: tint }} />
        </div>
        <h3 className="font-heading font-semibold text-lg">{title}</h3>
      </div>
      <div className="space-y-2 text-sm text-muted-foreground leading-relaxed">{children}</div>
    </div>
  );
}

export default function Methodology() {
  const [weights, setWeights] = useState({ momentum: 0.4, ma_trend: 0.35, rsi: 0.25 });
  const [source, setSource] = useState(null);

  useEffect(() => {
    api.get("/scores/latest").then(({ data }) => {
      if (data.weights) setWeights(data.weights);
      setSource(data.source);
    }).catch(() => {});
  }, []);

  const w = { m: Math.round(weights.momentum * 100), t: Math.round(weights.ma_trend * 100), r: Math.round(weights.rsi * 100) };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold font-heading tracking-tight flex items-center gap-2">
          <Sigma className="h-7 w-7 text-primary" /> How the Score Works
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          No black boxes. Every 1–100 score is a transparent, weighted blend of three indicators you can verify yourself.
          The "Why this pick?" card maps 1:1 to what's below.
        </p>
      </div>

      {/* The formula */}
      <div className="rounded-2xl border border-primary/20 bg-gradient-to-r from-emerald-500/10 via-sky-500/5 to-indigo-500/10 p-6">
        <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3">The Formula</p>
        <code className="block font-mono text-sm sm:text-base leading-relaxed">
          <span className="text-emerald-500">score</span> = {(weights.momentum).toFixed(2)}·<span className="text-primary">momentum</span>
          {" + "}{(weights.ma_trend).toFixed(2)}·<span className="text-sky-500">maTrend</span>
          {" + "}{(weights.rsi).toFixed(2)}·<span className="text-indigo-400">rsiGuardrail</span>
        </code>
        <p className="text-xs text-muted-foreground mt-3">
          Each component is first normalized to a 0–100 scale, then blended with the weights below and rounded to a 1–100 score.
        </p>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <WeightRing label="Momentum" pct={w.m} tint="#10B981" />
        <WeightRing label="MA Trend" pct={w.t} tint="#0EA5E9" />
        <WeightRing label="RSI Guardrail" pct={w.r} tint="#6366F1" />
      </div>

      <Section icon={Activity} title="1 · Momentum (20-day rate of change)" tint="#10B981">
        <p>We measure the percent price change over roughly the last 20 trading days (a swing-trade horizon).</p>
        <p className="font-mono text-foreground/90">momentumScore = clip( 50 + ROC% × 2.5 , 0, 100 )</p>
        <p>So a +20% run maps to ~100 and a −20% drop maps to ~0. Flat price sits near 50.</p>
      </Section>

      <Section icon={LineChart} title="2 · Moving-Average Trend (20 & 50 SMA)" tint="#0EA5E9">
        <p>We check trend alignment: price vs its 20-day average, and the 20-day vs the 50-day average.</p>
        <p className="font-mono text-foreground/90">maTrendScore = clip( 50 + (price/SMA20−1)×300 + (SMA20/SMA50−1)×300 , 0, 100 )</p>
        <p>A healthy uptrend (price &gt; SMA20 &gt; SMA50) scores high; a stacked downtrend scores low.</p>
      </Section>

      <Section icon={Gauge} title="3 · RSI as a Guardrail (not a buy signal)" tint="#6366F1">
        <p>RSI(14) is used for <strong className="text-foreground">mean-reversion protection</strong>, not naive "buy when high".
          The healthiest zone is roughly RSI 40–65.</p>
        <p className="font-mono text-foreground/90">rsiScore = clip( 100 − max(0, RSI−65)×3 − max(0, 40−RSI)×2 , 0, 100 )</p>
        <p>Overbought (RSI &gt; 65, especially &gt; 70) is penalized so we don't chase extended names; deeply oversold is trimmed too.</p>
      </Section>

      <Section icon={ShieldAlert} title="Risk Meter (separate from the score)" tint="#F59E0B">
        <p>Risk comes from volatility — the 14-day Average True Range as a percent of price — <strong className="text-foreground">not</strong> from the score.
          A high score can still be high risk.</p>
        <ul className="list-none space-y-1">
          <li><span className="text-emerald-500 font-semibold">Low</span> — ATR &lt; 2% of price</li>
          <li><span className="text-amber-500 font-semibold">Medium</span> — ATR 2–4%</li>
          <li><span className="text-red-500 font-semibold">High</span> — ATR &gt; 4%</li>
        </ul>
      </Section>

      <Section icon={Bot} title="AI Scorecard: data-grounded, not invented" tint="#6366F1">
        <p>On the AI Search page, the prediction percentage is <strong className="text-foreground">computed from the data</strong>,
          not made up by the AI. We anchor it on the model score, tilt it by the stock's relative strength vs the
          S&P 500, and apply an RSI overbought/oversold caution — then clamp it to a realistic 20–80% band.</p>
        <p>Gemini 3 Flash only <strong className="text-foreground">explains</strong> that fixed number in plain English;
          it is explicitly told not to alter or invent figures. The optional news-sentiment nudge is a small secondary
          adjustment layered on top, shown transparently.</p>
      </Section>

      <Section icon={Target} title="Accuracy: 'Beat the S&P 500'" tint="#10B981">
        <p>Our track record is deliberately honest and hard to game. Each day we rank the watchlist, take the top 5,
          and the next day a pick is only counted <strong className="text-foreground">"correct" if its return beat the S&P 500 (SPY)</strong> —
          not merely if it closed up.</p>
        <p>The rolling hit-rate you see on the Track Record page is that win count divided by total graded picks.</p>
      </Section>

      <Section icon={CalendarClock} title="Data & Refresh" tint="#0EA5E9">
        <p>Scores are <strong className="text-foreground">pre-computed by a scheduled job</strong> once per day (after the US market close, ~20:30 UTC),
          then served instantly from the database — so the app stays fast and doesn't hammer data providers.</p>
        <p>We fetch live daily-close data first{source ? <>, and today's run used <strong className="text-foreground">{source === "live" ? "live market data" : "the simulated fallback"}</strong></> : null}.
          If a provider rate-limits or fails, we fall back to a deterministic seeded simulation and flag it, rather than crashing the leaderboard.</p>
      </Section>

      <div className="rounded-2xl border border-amber-500/20 bg-amber-500/10 p-6 text-center">
        <p className="text-sm text-amber-600 dark:text-amber-400/90 font-medium">
          ⚠️ This methodology is for education only. Scores and picks are not recommendations and never involve real money.
        </p>
      </div>
    </div>
  );
}
